/* eslint-disable no-nested-ternary */
import { media } from '@prisjakt/ui-base';
import styled, { css } from 'styled-components';

import {
  Breakpoint,
  breakpointNames,
} from '@prisjakt/ui-base/styles/variables';
import { GUTTER } from 'src/areas/keyword-search-page/components/results/styles';

// We need to cover ALL breakpoints here since we use '`${breakpoint}Only`'
// If we left out xxl for example, then >1600px would look very weird as it wouldn't be covered by any breakpoint
// (This only works because the smallest and largest breakpoints are open in their respective direction)
export const ITEMS: Record<Breakpoint, number> = {
  small: 2,
  medium: 3,
  large: 4,
  xlarge: 4,
  xxlarge: 4,
};

const GAPS: Record<Breakpoint, number> = {
  small: 0.5,
  medium: 1,
  large: 1,
  xlarge: 1,
  xxlarge: 1,
};

// These are calculated for each render
const SIZES = {} as Record<Breakpoint, string>;
const GUTTERS = {} as Record<Breakpoint, string>;

/**
 * Certain experiments require a different grid layout, which is passed as props
 * Here we determine how many items to show depending on the passed props
 */
function getItemsForExperiments({
  isWide,
  isExtraWide,
}: {
  isWide?: boolean;
  isExtraWide?: boolean;
}) {
  if (isExtraWide) {
    return {
      large: 4,
      xlarge: 5,
      xxlarge: 6,
    };
  }
  if (isWide) {
    return {
      large: 5,
      xlarge: 5,
      xxlarge: 5,
    };
  }
  return {
    large: 4,
    xlarge: 4,
    xxlarge: 4,
  };
}

export const OffersGrid = styled.ul<{
  isWide?: boolean;
  /**
   * Whether to show 5/6/7 items on lage/xlarge/xxlarge
   * Currently used for the 'kwlp-wide-page' experiment
   */
  isExtraWide?: boolean;
  smallerProductCardsMobileExperiment?: boolean;
}>(props => {
  // We need to mutate the object here because src/areas/product-collection/components/products/layout-grid/layout-grid.tsx relies on the current layout configuration, and that depends on getItemsForExperiment
  // We don't want to implement the same logic twice, therefore we need to access this object from somewhere else
  Object.assign(ITEMS, getItemsForExperiments(props));

  // I'm not really happy with the current solution, but if we dynamically change ITEMS.large etc. for each render, then we also need to recalculate (gutter) sizes for each render
  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const bp of breakpointNames) {
    SIZES[bp] = `${100 / ITEMS[bp]}%`;
    GUTTERS[bp] = `${GAPS[bp] * (ITEMS[bp] - 1)}rem / ${ITEMS[bp]}`;
  }

  return css`
    @supports (grid-template-columns: repeat(auto-fill, minmax(auto, 50%))) {
      display: grid;
      flex: 1 0 auto;
      list-style: none;

      ${/* This only works because smallOnly and xxlargeOnly have no boundaries in each unset direction i.e. everything larger than xxlarge is considered xxlarge */ null}
      ${breakpointNames.map(breakpoint => {
        return media[`${breakpoint}Only`]`
          grid-gap: ${GAPS[breakpoint]}rem;
          grid-template-columns: repeat(
            auto-fill,
            minmax(auto, calc(${SIZES[breakpoint]} - ${GUTTERS[breakpoint]}))
          );
        `;
      })}
    }

    ${props.smallerProductCardsMobileExperiment &&
      media.smallOnly`
    margin: 0 -${props.isExtraWide ? '10px' : GUTTER};
    background-color: ${p => p.theme.colors.base200};
    grid-gap: 1px;
    gap: 1px;
    grid-template-columns: repeat(
      auto-fill,
      minmax(auto, calc(${SIZES.small} - 1px))
    );

    & > li {
      background-color: ${p => p.theme.bg.primary};

      & > article {
        & > a[href] {
          ${/* Fix border-radius for :hover box-shadow */ null}
          &::after {
            border-radius: 0;
          }

          ${
            /* Adjust margin of GridCardHeader, which is always 1st child */ null
          }
          & > div:nth-child(1) {
            margin: 0.5rem 0 0.3rem 0;
          }

          ${
            /* Adjust padding of GridCardContent, which is always 2nd child */ null
          }
          & > div:nth-child(2) {
            padding: 0 0.5rem 0.5rem 0.5rem;

            ${/* Product name is always 1st child */ null}
            & > span:nth-child(1) {
              margin-bottom: 0.2rem;
            }
          }
        }

        ${
          /* Offers don't contain a a[href], therefore we need to copy these styles */ null
        }
        ${/* Adjust margin of GridCardHeader, which is always 1st child */ null}
        & > div:nth-child(1) {
          margin: 0.5rem 0 0.3rem 0;
        }

        ${
          /* Adjust padding of GridCardContent, which is always 2nd child */ null
        }
        & > div:nth-child(2) {
          padding: 0 0.5rem 0.5rem 0.5rem;

          ${/* Product name is always 1st child */ null}
          & > span:nth-child(1) {
            margin-bottom: 0.2rem;
          }
        }
      }
    }
    `}

    /* Fallback to flex if grid-template-columns is not supported */
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  `;
});

export const OffersGridItem = styled.li``;
